import {
  DocumentBuilderLineVAT,
  PaymentMethodForm,
  SocialSecurityForm,
  WithholdingTaxForm,
} from '~/types/document-builder';
import { FlowPaymentTerms } from '~/types/flow';
import { StampType } from './invoicing';

export interface BookkeepingAccountsSettings {
  debtsSuppliers: string | null;
  debtsForeignSuppliers: string | null;
  creditsClients: string | null;
  creditsForeignClients: string | null;
  vatSales: string | null;
  vatPurchases: string | null;
  bankFees: string | null;
  netBills: string | null;
  vatBills: string | null;
  billsCash: string | null;
  billsCashContanti: string | null;
  billsCashElectronic: string | null;
  billsCashTicket: string | null;
  billsRevenueTicket: string | null;
  billsUnredeemedGift: string | null;
  revenueAccount: string | null;
  withholdingAccount: string | null;
  withholdingAccountSales: string | null;
  activeRoundingAccount: string | null;
  passiveRoundingAccount: string | null;
  vatSalesSplitPayment: string | null;
  previousYearVatCredit: string | null;
  vatSettlement: string | null;
  quarterlyVatInterest: string | null;
  socialSecuritySales: string | null;
  taxReturnVatRefund: string | null;
  taxReturnVatCompensation: string | null;
  accruedIncome: string | null;
  accruedExpense: string | null;
  prepaidExpense: string | null;
  deferredRevenue: string | null;
  invoicesToIssue: string | null;
  invoicesToReceive: string | null;
  creditNotesToIssue: string | null;
  creditNotesToReceive: string | null;
}

export enum VATPeriodAllocationLogic {
  ReceiptDate = 'RECEIPT_DATE',
  DocumentDate = 'DOCUMENT_DATE',
}

interface BookkeepingLIPESettings {
  controllingEntityVatNumber: string | null;
  groupVatSettlement: boolean | null;
  declarantFirstName: string | null;
  declarantLastName: string | null;
  declarantGender: 'MALE' | 'FEMALE' | null;
  declarantBirthDate: string | null;
  declarantBirthPlace: string | null;
  declarantBirthProvince: string | null;
  declarantFiscalCode: string | null;
  chargeCode: string | null;
  declarantCompanyFiscalCode: string | null;
  submissionCommitmentCode: '1' | '2' | null;
  intermediaryFiscalCode: string | null;
}

export interface BookkeepingIntraSettings {
  salesServiceCode: number | null;
}

interface BookkeepingFixedAssetSettings {
  /**
   * When enabled (default), the first-year book depreciation follows the fiscal rules:
   * half of the yearly rate for tangible assets, the full rate for intangible ones.
   * When disabled, the first-year book depreciation is prorated on the days elapsed
   * since the service start date, while the tax one keeps following the fiscal rules.
   */
  bookDepreciationFollowsTaxRules: boolean | null;
}

export interface BookkeepingSettings {
  accounts: BookkeepingAccountsSettings | null;
  vatPeriodAllocationLogic: VATPeriodAllocationLogic | null;
  lipe: BookkeepingLIPESettings | null;
  intra?: BookkeepingIntraSettings | null;
  /** Optional: omitted by the backend for companies that predate the flag. */
  fixedAsset?: BookkeepingFixedAssetSettings | null;
}

export interface CompanySettings {
  id: string;
  invoicing?: InvoicingSettings;
  bookkeeping?: BookkeepingSettings;
  autocategorization: boolean;
  multiAuthAllowed: boolean;
}

export interface PaymentSettings extends PaymentMethodForm {
  paymentDate: FlowPaymentTerms | null;
}

export interface StampSettings {
  type: StampType;
}

export interface InvoicingSettings {
  vat: Omit<DocumentBuilderLineVAT, 'lawReference'>;
  payment: PaymentSettings;
  socialSecurity: Omit<SocialSecurityForm, 'taxableAmount'> | null;
  withholdingTax: Omit<WithholdingTaxForm, 'taxableAmount' | 'taxableMode'>[];
  stamp: StampSettings | null;
  /** Optional: omitted by the backend for companies that predate the flag. */
  showPlus15PaymentTerms?: boolean;
}
